// Şub 27, 2026 — Ortak doküman önizleme/indirme helper'ı.
// Amaç: `/documents/{id}/download-url` presigned/CDN URL'ini alıp doğrudan
// <iframe>/<img>'e verebilmek — backend proxy'yi baypas edip CloudFront
// edge cache'den anında yükleme. Fallback: eski blob-fetch akışı.
//
// Şub 27, 2026 (CORS-safe mode): CloudFront/S3 CORS ayarları yapılana kadar
// SADECE `image/*` MIME tipleri direct CDN URL kullanır (browser <img> tag'ı
// CORS uygulamaz). PDF/DOC/XLS gibi XHR fetch gerektiren dosyalar eski blob
// akışına düşer → backend'den byte olarak çekilir → react-pdf sorunsuz açar.
//
// Kullanım:
//   const { url, isDirect } = await getPreviewUrl(api, doc.id);
//   setImageSrc(url);

import { api as defaultApi } from './auth';

// Direct URL için güvenli MIME grubu (browser <img>/<iframe> src'de CORS yok)
const CORS_SAFE_MIME_PREFIXES = ['image/'];

function isCorsSafeForDirectUrl(contentType) {
  if (!contentType) return false;
  const ct = String(contentType).toLowerCase();
  return CORS_SAFE_MIME_PREFIXES.some((p) => ct.startsWith(p));
}

/**
 * Bir doküman için önizleme URL'i döndürür.
 * Öncelik: (image/* için) presigned/CDN URL → başarısızsa/PDF/DOC için blob URL.
 * @param {object} api - axios instance
 * @param {string} docId - doküman id
 * @param {boolean} preview - true=inline, false=attachment (force download)
 * @returns {Promise<{ url: string|null, isDirect: boolean, blob?: Blob }>}
 */
export async function getPreviewUrl(api, docId, preview = true) {
  const a = api || defaultApi;
  // 1) Direct URL (CloudFront/presigned S3) — sadece image/* için güvenli
  try {
    const pres = await a.get(`/documents/${docId}/download-url?preview=${preview ? 'true' : 'false'}`);
    const payload = pres.data?.data;
    if (pres.data?.success && payload?.url) {
      // CORS-safe MIME (image/*) → doğrudan CDN URL kullan
      if (isCorsSafeForDirectUrl(payload.content_type)) {
        return { url: payload.url, isDirect: true };
      }
      // PDF/DOC/XLS vb → CORS gerekiyor; CDN ayarı henüz yoksa blob fetch et
      // (aşağıdaki fallback bloğu bunu yapar)
    }
    if (pres.data && pres.data.success === false && payload?.backend === null) {
      // Broken doc — blob fetch zaten 404 dönecek, direkt null
      return { url: null, isDirect: false };
    }
  } catch (_) {
    /* fallback aşağıda */
  }
  // 2) Blob fetch fallback (PDF/DOC/XLS + storage_backend!=s3 kayıtlar)
  try {
    const res = await a.get(`/documents/${docId}/download?preview=${preview ? 'true' : 'false'}`, {
      responseType: 'blob',
    });
    const blob = res.data;
    const url = URL.createObjectURL(blob);
    return { url, isDirect: false, blob };
  } catch (e) {
    return { url: null, isDirect: false };
  }
}

/**
 * Vehicle doküman/foto için önizleme URL'i.
 * @param {'docs'|'photos'} kind
 * CORS-safe mode: image/* → direct CDN, diğer → blob fetch.
 */
export async function getVehicleAssetUrl(api, vehicleId, kind, itemId, preview = true) {
  const a = api || defaultApi;
  try {
    const path = kind === 'photos'
      ? `/vehicles/${vehicleId}/photos/${itemId}/download-url`
      : `/vehicles/${vehicleId}/docs/${itemId}/download-url?preview=${preview ? 'true' : 'false'}`;
    const pres = await a.get(path);
    const payload = pres.data?.data;
    if (pres.data?.success && payload?.url) {
      // photos endpoint her zaman image; docs endpoint content_type field'ı ile geliyor
      if (kind === 'photos' || isCorsSafeForDirectUrl(payload.content_type)) {
        return { url: payload.url, isDirect: true };
      }
      // PDF/DOC docs → CORS ayarı yoksa blob fallback (aşağıda)
    }
  } catch (_) {
    /* fallback */
  }
  // Fallback: absolute backend URL for direct fetch
  const backend = process.env.REACT_APP_BACKEND_URL;
  try {
    const url = kind === 'photos'
      ? `${backend}/api/vehicles/${vehicleId}/photos/${itemId}`
      : `${backend}/api/vehicles/${vehicleId}/docs/${itemId}/download`;
    const r = await fetch(url);
    if (!r.ok) throw new Error('fetch failed');
    const blob = await r.blob();
    return { url: URL.createObjectURL(blob), isDirect: false, blob };
  } catch (e) {
    return { url: null, isDirect: false };
  }
}

/**
 * Bir doküman için tarayıcıya indirme tetikler.
 * Direct URL varsa <a href download> ile, yoksa blob yolu ile.
 */
export async function downloadDocument(api, doc) {
  const a = api || defaultApi;
  const filename = doc.original_name || doc.file_name || `document.${doc.file_type || 'bin'}`;
  // preview=false ile presigned URL (Content-Disposition=attachment)
  try {
    const pres = await a.get(`/documents/${doc.id}/download-url?preview=false`);
    const payload = pres.data?.data;
    if (pres.data?.success && payload?.url) {
      const link = document.createElement('a');
      link.href = payload.url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    }
    if (pres.data && pres.data.success === false && payload?.backend === null) {
      // Broken doc — blob fetch de 404 verecek, doğrudan başarısız dön
      return false;
    }
  } catch (_) { /* fallback */ }

  try {
    const res = await a.get(`/documents/${doc.id}/download?preview=false`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Bir URL'in blob URL olup olmadığını kontrol eder — cleanup için.
 */
export function isBlobUrl(url) {
  return typeof url === 'string' && url.startsWith('blob:');
}

/**
 * Eğer URL blob URL ise revoke eder (aksi halde no-op — direct URL'lerde güvenli).
 */
export function revokePreviewUrl(url) {
  if (isBlobUrl(url)) {
    try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
  }
}
